'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useWindowSize from '@alltrails/core/hooks/useWindowSize';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import { ShopifyPreview } from '@alltrails/shop';
import useSections from '../../hooks/useSections';
import { setSections } from '../../slices/sections';
import { useAppDispatch } from '../../utils/redux';
import type { HomepageSection, LocationData } from '../../utils/types';
import CommunityStats from '../blocks/CommunityStats';
import GenericGiftingBlock from '../blocks/GenericGiftingBlock';
import SeoLinks, { type SeoLinks as SeoLinksData } from '../blocks/SeoLinks';
import SponsoredLists from '../blocks/SponsoredLists';
import type { HitTrail } from '../blocks/TrailCarousel';
import UpgradeYourAdventures from '../blocks/UpgradeYourAdventures';
import TrailRiverTitle from '../TrailRiverTitle';
import styles from './styles.module.scss';

const ExploreAndDiscover = dynamic(() => import('../blocks/ExploreAndDiscover'));
const ActivitiesRiver = dynamic(() => import('../blocks/ActivitiesRiver'));
const TrailCarousel = dynamic(() => import('../blocks/TrailCarousel'));
const UgcSocialFooter = dynamic(() => import('../blocks/UgcSocialFooter'));

type Props = {
  sections?: HomepageSection[];
  latLng?: [number, number];
  locationData?: LocationData;
};

const BlockSwitch = ({ sections: initialSections = [], latLng, locationData }: Props) => {
  const { width } = useWindowSize();
  const languageRegionCode = useLanguageRegionCode();
  const dispatch = useAppDispatch();
  const reduxSections = useSelector((state: { sections?: { sections?: HomepageSection[] } }) => state.sections?.sections);
  const sections = reduxSections || initialSections;

  const experiments = useExperiments();
  const sendExposure = useExposureEvent('web-growth-rm-homepage-module-removal');
  const experimentVariant = experiments?.['web-growth-rm-homepage-module-removal']?.value;

  useEffect(() => {
    if (initialSections.length > 0 && !reduxSections) {
      dispatch(setSections(initialSections));
    }
  }, [initialSections, reduxSections, dispatch]);

  useEffect(() => {
    sendExposure();
  }, [sendExposure]);

  const processedSections = useSections({ initialSections: sections, latLng, locationData });

  // Exclude sections when treatment variant is active
  const excludedSections = experimentVariant === 'treatment' ? ['community_stats', 'attribute', 'gifting_upsell', 'ugc_social_footer'] : [];
  const filteredSections = processedSections.filter(section => !excludedSections.includes(section.section_type));

  const renderSection = useCallback(
    (section: HomepageSection, idx: number) => {
      switch (section.section_type) {
        case 'attribute': {
          const activities =
            !section.objects || !Array.isArray(section.objects)
              ? []
              : (section.objects as Array<{
                  title: string;
                  filters: { activity: string[] };
                  photo_urls: { square: { two: string; three: string } };
                }>);

          const activitiesTitle = section.title || '';

          return <ActivitiesRiver activities={activities} windowWidth={width} title={activitiesTitle} />;
        }

        case 'seo_links': {
          const seoLinksData = (section.data as SeoLinksData) || [];
          if (seoLinksData.length === 0) return null;
          return <SeoLinks data={seoLinksData} />;
        }

        case 'community_stats':
          return <CommunityStats />;

        case 'upgrade_your_adventures':
          return <UpgradeYourAdventures windowWidth={width} />;

        case 'sponsored_lists':
          return <SponsoredLists title={section.title} collections={section.collections} />;

        case 'gifting_upsell':
          return <GenericGiftingBlock />;

        case 'explore_and_discover':
          return <ExploreAndDiscover />;

        case 'merchandise_store': {
          if (section.products && section.products.length > 0) {
            return <ShopifyPreview shopifyProducts={section.products} className={styles.shopifyPreview} />;
          }
          return null;
        }

        case 'trail_list': {
          return (
            <TrailCarousel
              hits={section.hits}
              title={<TrailRiverTitle section={section} idx={idx} locationData={locationData} languageRegionCode={languageRegionCode} />}
            />
          );
        }

        case 'googlebot_trail_list': {
          const hits = section.trails.concat({
            exploreMoreUrl: `/explore/${section.country_slug}`
          } as HitTrail);

          return <TrailCarousel hits={hits} title={section.title || ''} />;
        }

        case 'ugc_social_footer':
          return <UgcSocialFooter />;

        default:
          return null;
      }
    },
    [width, languageRegionCode, locationData]
  );

  return (
    <>
      {filteredSections.map((section, index) => (
        <div key={`${section.section_type}-${index}`}>{renderSection(section, index)}</div>
      ))}
    </>
  );
};

export default BlockSwitch;
